import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Drawer, Segmented, Select, Input, Modal, Tag, Spin, Radio } from 'antd';
import { PlusOutlined, WifiOutlined, SendOutlined, DeleteOutlined, CloudSyncOutlined, NodeIndexOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { userRequest } from '../../config';
import { useSelector } from 'react-redux';
import { draftKey, readDraft, writeDraft, clearDraft } from '../../utils/formDraft';
import { repartir, poidsDesSegments } from './repartition';
import { NdoePage, NdoePageHeader, NdoeError } from '../../components/ndoe';
import * as store from './offlineStore';
import { runSync } from './feuilleDepenseSync';
import useConnectivite from './useConnectivite';
import './feuilleDepense.css';

/*
  Feuille de dépense du jour — écran du déclarant (livreur par défaut).

  Le principe tenu par cet écran : la journée d'un livreur est un rituel, pas une
  saisie. 69 % des trajets déclarés sur trois ans sont quatre couples répétés.
  Ils doivent donc se confirmer d'UN TAP, pas se composer champ par champ.

  MODE HORS LIGNE (cet écran) : la SAISIE ne dépend jamais du réseau. Chaque
  ajout/retrait écrit d'abord un BROUILLON LOCAL (localStorage), qui est la source
  de vérité de l'affichage. La synchronisation avec le serveur se fait à des
  moments définis — ouverture en ligne, retour du réseau, soumission — et remplace
  les lignes serveur par les lignes locales (voir feuilleDepenseSync : idempotent,
  reprise après coupure, feuille déjà validée gérée). Le référentiel et les
  commandes sont mis en cache pour que l'écran fonctionne sans réseau.

  ATTENTION (piège éprouvé sur ce dépôt) : ne JAMAIS définir de sous-composant à
  l'intérieur du rendu — React démonte l'arbre à chaque frappe et le champ perd
  le focus. Tout ce qui suit est donc soit au niveau module, soit du JSX inline.
*/

const TYPES = [
  { value: 'AUTRE', label: 'Déplacement' },
  { value: 'LIVRAISON', label: 'Livraison' },
  { value: 'COLIS', label: 'Colis / agence' },
];

// Pourquoi une course coute plus que d'habitude. Liste COURTE et fermee : dans
// six mois on voudra compter les hausses dues a la pluie ou aux embouteillages,
// et du texte libre seul ne se compte pas. « Autre » garde la porte ouverte.
const MOTIFS_ECART = [
  'Pluie',
  'Embouteillages',
  'Heure tardive',
  'Trajet allonge (detour)',
  'Charge encombrante',
  'Autre',
];

// Tri de la liste des commandes du jour. CLIENT et non serveur : la liste est
// plafonnee a 80, le tri doit etre instantane et surtout marcher HORS LIGNE —
// c'est en tournee qu'on cherche une commande. (Le serveur porte la meme liste
// blanche : elle decide QUELLES 80 reviennent en cas de troncature.)
const TRIS = [
  { cle: 'prise', label: 'Prise en charge', cmp: null },
  { cle: 'cliente', label: 'Cliente (A-Z)', cmp: (a, b) => String(a.nom_client || '').localeCompare(String(b.nom_client || ''), 'fr') },
  { cle: 'zone', label: 'Zone', cmp: (a, b) => String(a.zone || 'zzz').localeCompare(String(b.zone || 'zzz'), 'fr') },
  { cle: 'paires', label: 'Nombre de paires', cmp: (a, b) => (Number(b.paires) || 0) - (Number(a.paires) || 0) },
  { cle: 'numero', label: 'N° de commande', cmp: (a, b) => (Number(b.id_commande) || 0) - (Number(a.id_commande) || 0) },
];

const MONTANT_COMMUNICATION_DEFAUT = 1500; // médiane observée sur 438 relevés

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

// Jour local au format AAAA-MM-JJ. L'APK tourne sur le téléphone à Kinshasa
// (UTC+1, comme le shop) : la date locale du téléphone = la date du serveur.
// (Pas de dayjs : proscrit sur ce dépôt — il gèle la WebView.)
const aujourdhui = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
// Libellés d'un jour AAAA-MM-JJ (sans dayjs). Court pour les puces, long pour le bandeau.
const libelleJourCourt = (ymd) => {
  try { return new Date(`${ymd}T00:00:00`).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }); }
  catch (e) { return ymd; }
};
const libelleJourLong = (ymd) => {
  try { return new Date(`${ymd}T00:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }); }
  catch (e) { return ymd; }
};

// Trajet lisible d'une ligne (même forme locale et serveur).
const libelleTrajet = (l) => {
  if (!l.depart?.nom && !l.arrivee?.nom) return 'Forfait téléphone';
  return `${l.depart?.nom || '?'} → ${l.arrivee?.nom || '?'}`;
};
const detailLigne = (l) => {
  const complements = [l.moyen, l.nom_client].filter(Boolean).join(' · ');
  if (complements) return complements;
  if (l.type === 'COMMUNICATION') return 'Crédit d’appel ou internet';
  return TYPES.find((t) => t.value === l.type)?.label || '';
};

const pointNom = (ref, id) => (ref.points || []).find((p) => p.id === id)?.nom || null;
const moyenNom = (ref, id) => (ref.moyens || []).find((m) => m.id === id)?.libelle || null;
const commandeNom = (cmds, id) => (cmds || []).find((c) => c.id_commande === id)?.nom_client || null;

// Dernière arrivée connue parmi des lignes locales — sert de départ (affichage)
// à la ligne suivante. On ignore les lignes sans trajet (communication).
const dernierePosition = (lignes) => {
  for (let i = lignes.length - 1; i >= 0; i -= 1) {
    if (lignes[i].arrivee?.id || lignes[i].arrivee?.nom) return lignes[i].arrivee;
  }
  return null;
};

// Construit une ligne LOCALE (affichage + champs bruts pour la synchro) à partir
// d'une saisie.
//
// ⚠ LE DÉPART EST TOUJOURS DÉRIVÉ, JAMAIS CELUI QU'ON A TOUCHÉ.
//
// On part d'où l'on est : l'arrivée du trajet précédent, ou le point d'ancrage
// pour le premier trajet de la journée. C'est la règle du serveur
// (`resoudreDepart`) et la synchro n'envoie de toute façon aucun départ — le
// serveur rechaîne systématiquement.
//
// L'affichage local, lui, prenait le départ écrit sur le favori. Toucher
// « Siège → Magasin » alors qu'on est à Lalou montrait « Siège → Magasin », et
// la ligne devenait « Lalou → Magasin » après synchro (constaté en mode avion,
// 31/07). Le libellé changeait sous les yeux du livreur, et le montant validé
// correspondait au tarif d'un AUTRE couple — de quoi déclencher une fausse
// alerte d'écart à la validation.
//
// Le favori reste un raccourci vers une DESTINATION et un montant ; son départ
// est une étiquette, pas une affirmation sur l'endroit où l'on se trouve.
const construireLigne = (brut, ref, cmds, lignesExistantes) => {
  const arriveeId = brut.id_point_arrivee || null;
  const arriveeNom = arriveeId ? pointNom(ref, arriveeId) : (brut.libelle_arrivee || null);
  const estTrajet = brut.type !== 'COMMUNICATION';
  const ancrage = ref.point_ancrage
    ? { id: ref.point_ancrage, nom: pointNom(ref, ref.point_ancrage), libre: false }
    : null;
  const prec = estTrajet ? (dernierePosition(lignesExistantes) || ancrage) : null;
  // Un segment de PARCOURS déclare son départ et le transmet ; une ligne simple
  // n'en transmet aucun et se fait rechaîner par le serveur.
  const departResolu = (brut.id_parcours && brut.id_point_depart)
    ? { id: brut.id_point_depart, nom: pointNom(ref, brut.id_point_depart), libre: false }
    : prec;
  return {
    uid: store.nouveauUid(),
    type: brut.type,
    // Le départ résolu, pas celui saisi : c'est lui qu'affiche la feuille, et
    // pour un PARCOURS c'est lui qui part au serveur (chaque segment déclare
    // son départ). Pour une ligne simple, la synchro n'envoie rien et le
    // serveur rechaîne — le résultat est le même.
    id_point_depart: departResolu?.id || null,
    id_point_arrivee: arriveeId,
    libelle_depart: null,
    libelle_arrivee: arriveeId ? null : (brut.libelle_arrivee || null),
    id_moyen: brut.id_moyen || null,
    id_commande: brut.type === 'LIVRAISON' ? (brut.id_commande || null) : null,
    montant_fc: Number(brut.montant_fc) || 0,
    note: brut.note || null,
    // Regroupement multi-arrets : porte par les lignes LOCALES depuis le
    // passage du parcours en hors-ligne.
    id_parcours: brut.id_parcours || null,
    // Champs d'affichage : on montre EXACTEMENT ce que le serveur enregistrera.
    //  - segment de parcours : son départ est déclaré et transmis, le serveur
    //    l'honore tel quel (resoudreDepart, règle 1) ;
    //  - ligne simple : rien n'est transmis, le serveur rechaîne → `prec`.
    depart: estTrajet ? departResolu : { id: null, nom: null },
    arrivee: estTrajet
      ? { id: arriveeId, nom: arriveeNom, libre: !arriveeId && !!arriveeNom }
      : { id: null, nom: null },
    moyen: moyenNom(ref, brut.id_moyen),
    nom_client: brut.type === 'LIVRAISON' ? commandeNom(cmds, brut.id_commande) : null,
  };
};

// Adopte une ligne SERVEUR (déjà enrichie : départ rechaîné, noms) en ligne
// locale — après une synchro réussie, le serveur fait autorité pour l'affichage.
const adopterLigneServeur = (l) => ({
  uid: `srv-${l.id_ligne}`,
  id_ligne: l.id_ligne,
  type: l.type,
  id_point_depart: l.depart?.id || null,
  id_point_arrivee: l.arrivee?.id || null,
  libelle_depart: l.depart?.libre ? l.depart?.nom : null,
  libelle_arrivee: l.arrivee?.libre ? l.arrivee?.nom : null,
  id_moyen: l.id_moyen || null,
  id_commande: l.id_commande || null,
  id_parcours: l.id_parcours || null, // regroupement multi-arrêts
  montant_fc: Number(l.montant_fc) || 0,
  note: l.note || null,
  depart: l.depart || { id: null, nom: null },
  arrivee: l.arrivee || { id: null, nom: null },
  moyen: l.moyen || null,
  nom_client: l.nom_client || null,
});

// Regroupe les lignes CONSÉCUTIVES d'un même parcours en un seul item pliable
// (les segments A→B→C…). Une ligne isolée reste un item simple.
const grouperLignes = (lignes) => {
  const items = [];
  for (const l of lignes) {
    const dernier = items[items.length - 1];
    if (l.id_parcours && dernier && dernier.parcours && dernier.id_parcours === l.id_parcours) {
      dernier.segments.push(l);
      dernier.total += Number(l.montant_fc) || 0;
    } else if (l.id_parcours) {
      items.push({ parcours: true, id_parcours: l.id_parcours, id_commande: l.id_commande, nom_client: l.nom_client, segments: [l], total: Number(l.montant_fc) || 0, uid: `parc-${l.id_parcours}` });
    } else {
      items.push(l);
    }
  }
  return items;
};

const totalDe = (lignes) => (lignes || []).reduce((s, l) => s + (Number(l.montant_fc) || 0), 0);

const FeuilleDepense = () => {
  const currentUser = useSelector((s) => s.user?.currentUser);
  const idUser = currentUser?.id || 0;
  // RATTRAPAGE : le jour est désormais un ÉTAT (défaut = aujourd'hui). Tout le
  // reste (brouillon local, synchro, feuille) est déjà indexé par `date` — il
  // suffisait de le rendre variable. Un jour passé permet de déclarer après coup.
  const [date, setDate] = useState(() => aujourdhui());
  const [datesActivite, setDatesActivite] = useState([]);
  const [parcoursOuverts, setParcoursOuverts] = useState({}); // id_parcours -> déplié
  // Constructeur de PARCOURS multi-arrêts (MVP réseau).
  const [parcOuvert, setParcOuvert] = useState(false);
  const [parcCommande, setParcCommande] = useState(null);
  const [parcArrets, setParcArrets] = useState([]);   // [{ id_point, nom }]
  const [parcMontants, setParcMontants] = useState([]); // longueur = arrets - 1
  const [parcEnvoi, setParcEnvoi] = useState(false);
  const [parcUid, setParcUid] = useState(null); // uid figé à l'ouverture → retry sans doublon
  // UNE COMMANDE PAR ARRÊT. Une tournée dessert rarement une seule cliente :
  // dépôt → chez A → chez B → retour. `parcCommande` reste la valeur par
  // défaut ; `parcCommandes[i]` est la commande du segment i quand elle diffère.
  const [parcCommandes, setParcCommandes] = useState([]);
  // MONTANT GLOBAL NÉGOCIÉ. Sur le terrain on discute UN prix pour toute la
  // course, pas segment par segment. On saisit alors le total, et il est
  // réparti — voir `repartirGlobal`.
  const [parcGlobal, setParcGlobal] = useState(false);
  const [parcMontantGlobal, setParcMontantGlobal] = useState('');
  // « Cash reçu » (fond de caisse / provision transport) + recette du jour :
  // ensemble ils reconstituent le rapport financier envoyé sur WhatsApp.
  const [provisions, setProvisions] = useState([]);
  const [recu, setRecu] = useState({ montant_fc: 0, montant_usd: 0, nombre: 0 });
  const [recette, setRecette] = useState(null); // encaissé déclaré aux clientes (module livraison)
  const [cashOuvert, setCashOuvert] = useState(false);
  const [cashMotif, setCashMotif] = useState('TRANSPORT');
  const [cashMontant, setCashMontant] = useState('');
  const [cashCommande, setCashCommande] = useState(null);
  const [cashEnvoi, setCashEnvoi] = useState(false);
  const estAujourdhui = date === aujourdhui();
  const { enLigne, signalerEchecReseau, signalerSucces } = useConnectivite();

  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);

  const [referentiel, setReferentiel] = useState({ points: [], moyens: [], tarifs: [], point_ancrage: null });
  const [favoris, setFavoris] = useState([]);
  const [commandes, setCommandes] = useState([]);
  const [triCommandes, setTriCommandes] = useState('prise');
  // Le brouillon local est la SOURCE DE VÉRITÉ de l'affichage.
  const [brouillon, setBrouillon] = useState({ lignes: [], statut: 'BROUILLON' });
  // Métadonnées serveur (statut réel de la feuille, motif de renvoi).
  const [feuilleServeur, setFeuilleServeur] = useState(null);
  const [sync, setSync] = useState({ etat: 'inconnu', message: null }); // synchro | attente | hors_ligne | conflit

  // Formulaire d'ajout (tiroir)
  const [ouvert, setOuvert] = useState(false);
  const [type, setType] = useState('AUTRE');
  const [modeLieu, setModeLieu] = useState('liste');
  const [idArrivee, setIdArrivee] = useState(null);
  const [libelleArrivee, setLibelleArrivee] = useState('');
  const [montant, setMontant] = useState('');
  const [motifEcart, setMotifEcart] = useState(undefined);
  const [idMoyen, setIdMoyen] = useState(null);
  const [idCommande, setIdCommande] = useState(null);

  const syncLock = useRef(false);       // empêche deux synchros simultanées
  const debounce = useRef(null);
  const brouillonRef = useRef(brouillon);
  brouillonRef.current = brouillon;

  const lignes = brouillon.lignes;
  const soumiseOuValidee = ['SOUMISE', 'VALIDEE'].includes(feuilleServeur?.statut);
  const modifiable = !soumiseOuValidee;
  const totalFc = totalDe(lignes);

  const persister = useCallback((maj) => {
    setBrouillon((prec) => {
      const suivant = typeof maj === 'function' ? maj(prec) : maj;
      store.ecrireBrouillon(idUser, date, suivant);
      return suivant;
    });
  }, [idUser, date]);

  // Reconstruit le brouillon (et les métadonnées) à partir de l'état serveur.
  const adopterServeur = useCallback((jour) => {
    const srvLignes = (jour.lignes || []).map(adopterLigneServeur);
    const suivant = { lignes: srvLignes, statut: 'BROUILLON' };
    store.ecrireBrouillon(idUser, date, suivant);
    setBrouillon(suivant);
    setFeuilleServeur(jour.feuille || null);
    if (jour.favoris) { setFavoris(jour.favoris); store.ecrireFavoris(idUser, jour.favoris); }
  }, [idUser, date]);

  // --- Synchronisation (verrouillée : jamais deux en parallèle) --------------
  const synchroniser = useCallback(async ({ soumettre: veutSoumettre = false } = {}) => {
    if (syncLock.current) return { ok: false, occupe: true };
    syncLock.current = true;
    try {
      const courant = brouillonRef.current;
      const aEnvoyer = veutSoumettre ? { ...courant, statut: 'A_SOUMETTRE' } : courant;
      const r = await runSync({ requester: userRequest, date, brouillon: aEnvoyer });

      if (r.horsLigne) {
        signalerEchecReseau();
        // On garde l'intention de soumettre pour la rejouer au retour du réseau.
        if (veutSoumettre) persister((b) => ({ ...b, statut: 'A_SOUMETTRE' }));
        setSync({ etat: 'hors_ligne', message: null });
        return r;
      }
      signalerSucces();

      if (r.conflit) {
        // La feuille a basculé côté serveur pendant l'opération : on recharge la
        // vérité et on abandonne le brouillon local pour ce jour.
        try {
          const { data } = await userRequest.get('/api/feuille-depense/mine/jour', { params: { date } });
          adopterServeur(data);
        } catch (e) { /* on gardera l'affichage courant */ }
        setSync({ etat: 'conflit', message: r.erreur });
        return r;
      }

      // Succès : on relit la vérité serveur (départs rechaînés, statut réel) et on
      // l'adopte — local et serveur convergent.
      try {
        const { data } = await userRequest.get('/api/feuille-depense/mine/jour', { params: { date } });
        adopterServeur(data);
      } catch (e) { /* la synchro a réussi ; l'affichage sera rafraîchi plus tard */ }
      // Message = le serveur avait déjà bougé (feuille soumise/validée) : la
      // bannière est masquée sur une feuille figée, donc on le dit au toast pour
      // que le déclarant sache que sa dernière saisie n'a pas été prise.
      if (r.adopte && r.message) toast.info(r.message);
      setSync({ etat: 'synchro', message: r.message || null });
      return { ...r, ok: true };
    } finally {
      syncLock.current = false;
    }
  }, [date, persister, adopterServeur, signalerEchecReseau, signalerSucces]);

  // Programme une synchro différée (coalesce les taps rapides). Uniquement en
  // ligne : hors ligne, on n'insiste pas, la bannière indique « en attente ».
  const programmerSync = useCallback(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => { synchroniser(); }, 1500);
  }, [synchroniser]);

  // --- Chargement initial ----------------------------------------------------
  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);

    // 1) Référentiel : serveur si possible, sinon cache. Sans lui (jamais chargé),
    //    l'écran ne peut pas fonctionner hors ligne.
    let ref = store.lireReferentiel();
    // Le cache hors-ligne des commandes ne vaut que pour AUJOURD'HUI ; pour un
    // jour de rattrapage on repart d'une liste vide (le réseau la remplira).
    let cmds = estAujourdhui ? (store.lireCommandes() || []) : [];
    let enLigneEffectif = true;
    try {
      const [r, c] = await Promise.all([
        userRequest.get('/api/feuille-depense/referentiel'),
        userRequest.get('/api/feuille-depense/mine/commandes', { params: { date } }),
      ]);
      ref = r.data; store.ecrireReferentiel(r.data);
      cmds = c.data?.commandes || [];
      if (estAujourdhui) store.ecrireCommandes(cmds); // ne pas polluer le cache du jour
      if (Array.isArray(c.data?.dates)) setDatesActivite(c.data.dates);
      signalerSucces();
    } catch (e) {
      enLigneEffectif = false;
      signalerEchecReseau();
    }

    if (!ref || !ref.points?.length) {
      setErreur('Connectez-vous une première fois avec du réseau pour charger le référentiel des trajets.');
      setChargement(false);
      return;
    }
    setReferentiel(ref);
    setCommandes(cmds || []);

    // Favoris : d'abord le cache (utile hors ligne), rafraîchis ensuite si en ligne.
    setFavoris(store.lireFavoris(idUser));

    // 2) Brouillon local du jour + réconciliation avec le serveur si en ligne.
    const local = store.lireBrouillon(idUser, date);
    if (local) { setBrouillon(local); }

    if (enLigneEffectif) {
      try {
        const { data } = await userRequest.get('/api/feuille-depense/mine/jour', { params: { date } });
        if (data.favoris) { setFavoris(data.favoris); store.ecrireFavoris(idUser, data.favoris); }
        // Cash reçu du jour (fond de caisse + provisions) — sert au rapport financier.
        setProvisions(data.provisions || []);
        setRecu(data.recu || { montant_fc: 0, montant_usd: 0, nombre: 0 });
        // Recette = ce que le livreur déclare avoir encaissé chez les clientes
        // (module livraison). Best-effort : son absence ne bloque pas la feuille.
        userRequest.get('/api/mobile-delivery/mine/activite-jour', { params: { start: date, end: date } })
          .then((r) => setRecette(r.data?.argent?.total_encaisse_declare ?? null))
          .catch(() => setRecette(null));
        const localAEdits = local && local.lignes && local.lignes.length > 0;
        if (['SOUMISE', 'VALIDEE'].includes(String(data.feuille?.statut))) {
          adopterServeur(data);                 // la journée est close : on adopte
          setSync({ etat: 'synchro', message: null });
        } else if (localAEdits) {
          setFeuilleServeur(data.feuille || null);
          setSync({ etat: 'attente', message: null });
          synchroniser();                        // pousser les saisies locales
        } else {
          adopterServeur(data);                  // pas d'édits locaux : on prend le serveur
          setSync({ etat: 'synchro', message: null });
        }
      } catch (e) {
        setSync({ etat: 'hors_ligne', message: null });
      }
    } else {
      setSync({ etat: local && local.lignes?.length ? 'attente' : 'hors_ligne', message: null });
    }
    setChargement(false);
  }, [idUser, date, adopterServeur, synchroniser, signalerSucces, signalerEchecReseau]);

  useEffect(() => { charger(); /* au montage ET à chaque changement de jour (rattrapage) */ }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  // Retour du réseau : on rejoue la synchro (et la soumission si elle attendait).
  useEffect(() => {
    if (!enLigne) return;
    const b = brouillonRef.current;
    if (b.lignes?.length && (sync.etat === 'hors_ligne' || sync.etat === 'attente')) {
      synchroniser({ soumettre: b.statut === 'A_SOUMETTRE' });
    }
  }, [enLigne]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Position courante / tarifs (pour le pré-remplissage) ------------------
  const positionCourante = useMemo(() => dernierePosition(lignes)?.id || null, [lignes]);
  const tarifDe = useCallback((depart, arrivee) => {
    if (!depart || !arrivee) return null;
    return (referentiel.tarifs || []).find((t) => t.depart === depart && t.arrivee === arrivee) || null;
  }, [referentiel.tarifs]);

  const reinitialiser = () => {
    setType('AUTRE'); setModeLieu('liste'); setIdArrivee(null);
    setLibelleArrivee(''); setMontant(''); setIdMoyen(null); setIdCommande(null); setMotifEcart(undefined);
  };

  // --- Ajout / retrait : LOCAL d'abord, synchro ensuite ----------------------
  const ajouterLigne = (brut) => {
    persister((b) => ({
      ...b,
      lignes: [...b.lignes, construireLigne(brut, referentiel, commandes, b.lignes)],
    }));
    setSync((s) => ({ ...s, etat: enLigne ? 'attente' : 'hors_ligne' }));
    if (enLigne) programmerSync();
  };

  const ajouterFavori = (f) => {
    ajouterLigne({ type: 'AUTRE', id_point_depart: f.depart, id_point_arrivee: f.arrivee, montant_fc: f.montant, id_moyen: f.id_moyen || null });
    toast.success(`${f.nom_depart} → ${f.nom_arrivee} ajouté.`);
  };
  const ajouterCommunication = () => {
    ajouterLigne({ type: 'COMMUNICATION', montant_fc: MONTANT_COMMUNICATION_DEFAUT });
    toast.success('Forfait ajouté.');
  };
  const validerFormulaire = () => {
    const brut = { type, montant_fc: Number(montant) };
    if (modeLieu === 'liste') brut.id_point_arrivee = idArrivee;
    else brut.libelle_arrivee = libelleArrivee.trim();
    if (idMoyen) brut.id_moyen = idMoyen;
    if (type === 'LIVRAISON') brut.id_commande = idCommande;
    // Motif d'un montant au-dessus de l'habituel : porté par `note`, que le
    // serveur accepte depuis toujours mais que le front n'envoyait jamais.
    if (ecartTarif && motifEcart) brut.note = motifEcart;
    ajouterLigne(brut);
    toast.success('Trajet ajouté.');
    setOuvert(false); reinitialiser();
  };

  const supprimer = (l) => Modal.confirm({
    title: 'Retirer cette dépense ?',
    content: `${libelleTrajet(l)} — ${fmt(l.montant_fc)} FC`,
    okText: 'Retirer', cancelText: 'Annuler', okButtonProps: { danger: true },
    onOk: () => {
      persister((b) => ({ ...b, lignes: b.lignes.filter((x) => x.uid !== l.uid) }));
      setSync((s) => ({ ...s, etat: enLigne ? 'attente' : 'hors_ligne' }));
      if (enLigne) programmerSync();
    },
  });

  const soumettre = () => Modal.confirm({
    title: 'Soumettre la journée ?',
    content: enLigne
      ? `${lignes.length} dépense(s), ${fmt(totalFc)} FC. Vous ne pourrez plus les modifier.`
      : `${lignes.length} dépense(s), ${fmt(totalFc)} FC. Hors ligne : la journée sera envoyée dès le retour du réseau.`,
    okText: 'Soumettre', cancelText: 'Pas encore',
    onOk: async () => {
      setEnCours(true);
      try {
        if (!enLigne) {
          persister((b) => ({ ...b, statut: 'A_SOUMETTRE' }));
          setSync({ etat: 'hors_ligne', message: null });
          toast.info('Journée en attente : elle partira dès le réseau revenu.');
          return;
        }
        const r = await synchroniser({ soumettre: true });
        if (r.ok && r.soumis) toast.success('Journée envoyée à la secrétaire.');
        else if (r.conflit) toast.info(r.erreur || 'Feuille déjà traitée par le bureau.');
        else if (r.horsLigne) toast.info('Réseau perdu : la journée partira dès son retour.');
      } finally {
        setEnCours(false);
      }
    },
  });

  const choisirArrivee = (id) => {
    setIdArrivee(id);
    const t = tarifDe(positionCourante, id);
    if (t && !montant) setMontant(String(t.median));
  };

  // Tarif du trajet en cours de saisie + situation du montant dans sa
  // fourchette (0 % = p25, 100 % = p75, borné pour rester lisible).
  const tarifCourant = tarifDe(positionCourante, idArrivee);
  const montantNum = Number(montant) || 0;
  const positionDansFourchette = (() => {
    if (!tarifCourant || !montantNum) return null;
    const { p25, p75 } = tarifCourant;
    if (!(p75 > p25)) return 50;
    return Math.max(0, Math.min(100, ((montantNum - p25) / (p75 - p25)) * 100));
  })();
  // « Hors fourchette » = au-dessus du p75 seulement : payer MOINS que
  // l'habituel n'a pas à être justifié.
  const ecartTarif = !!(tarifCourant && montantNum && montantNum > tarifCourant.p75);

  // --- « Cash reçu » : fond de caisse / provision de transport --------------
  const ouvrirCash = () => {
    setCashMotif('TRANSPORT');
    setCashMontant('');      // pas de pré-remplissage : le montant varie (médiane 10 000 FC)
    setCashCommande(null);
    setCashOuvert(true);
  };
  const envoyerCash = async () => {
    const fc = Number(String(cashMontant).replace(/\D/g, ''));
    if (!(fc > 0)) { toast.error('Indiquez le montant reçu.'); return; }
    setCashEnvoi(true);
    try {
      await userRequest.post('/api/feuille-depense/mine/provision', {
        date, motif: cashMotif, montant_fc: fc, id_commande: cashCommande || null,
      });
      setCashOuvert(false);
      toast.success(`Cash reçu enregistré : ${fmt(fc)} FC.`);
      await charger();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Enregistrement impossible.');
    } finally { setCashEnvoi(false); }
  };
  const supprimerProvision = (p) => Modal.confirm({
    title: 'Retirer ce cash reçu ?',
    content: `${fmt(p.montant_fc)} FC${p.nom_client ? ` · ${p.nom_client}` : ''}`,
    okText: 'Retirer', cancelText: 'Annuler', okButtonProps: { danger: true },
    onOk: async () => {
      try {
        await userRequest.delete(`/api/feuille-depense/mine/provision/${p.id_provision}`);
        await charger();
      } catch (e) { toast.error(e.response?.data?.error || 'Suppression impossible.'); }
    },
  });
  // Rapport financier, à la forme des messages WhatsApp :
  //   Cash reçu + Recette = Total ; Total − Dépenses = Reste à remettre.
  // ⚠ CHAQUE DEVISE SE CALCULE SÉPARÉMENT (les rapports écrivent « Reste 200$+3000fc ») :
  // la recette des ventes est en DOLLARS, la feuille de trajets en FRANCS. Les
  // additionner donnerait un total faux — on ne convertit pas, on juxtapose.
  const recuFc = Number(recu.montant_fc) || 0;
  const recuUsd = Number(recu.montant_usd) || 0;
  const recetteUsd = Number(recette) || 0;   // encaissé déclaré aux clientes ($)
  const resteFc = recuFc - totalFc;          // francs : reçu − dépenses de transport
  const resteUsd = recetteUsd + recuUsd;     // dollars : la recette reste due telle quelle

  // --- Constructeur de PARCOURS multi-arrêts (MVP réseau) -------------------
  const pointsRef = referentiel.points || [];
  const ouvrirParcours = () => {
    // Le 1er arrêt part de la DERNIÈRE POSITION connue de la journée (fin du
    // dernier trajet déclaré) plutôt que du 1er point du référentiel : c'est là
    // que le livreur se trouve réellement. Repli : point le plus fréquent.
    // Repli : le POINT D'ANCRAGE publié par le serveur — le même que celui qu'il
    // utiliserait pour le premier trajet de la journée. Avant, on prenait le
    // premier point du référentiel (le plus fréquent), ce qui pouvait différer
    // dès que l'ancrage est configuré.
    const pos = positionCourante || referentiel.point_ancrage || null;
    const depart = pos
      ? { id_point: pos, nom: pointNom(referentiel, pos) }
      : (pointsRef[0] ? { id_point: pointsRef[0].id, nom: pointsRef[0].nom } : { id_point: null, nom: null });
    setParcArrets([depart]);
    setParcMontants([]);
    setParcCommandes([]);
    setParcGlobal(false);
    setParcMontantGlobal('');
    setParcCommande(idCommande || null);
    // UID FIGÉ À L'OUVERTURE (pas au POST) : si l'envoi échoue (réseau) et que le
    // livreur réessaie, le MÊME uid repart → l'idempotence serveur joue et aucun
    // parcours n'est créé en double.
    setParcUid(store.nouveauUid());
    setParcOuvert(true);
  };
  const majArret = (i, id) => setParcArrets((a) => a.map((x, k) => (k === i ? { id_point: id, nom: pointNom(referentiel, id) } : x)));
  const ajouterArret = () => {
    setParcArrets((a) => [...a, { id_point: null, nom: null }]);
    setParcMontants((m) => [...m, '']);
    setParcCommandes((c) => [...c, null]);   // hérite du défaut tant qu'on ne choisit rien
  };
  const retirerArret = (i) => {
    setParcArrets((a) => a.filter((_, k) => k !== i));
    const seg = Math.max(0, i - 1);
    setParcMontants((m) => m.filter((_, k) => k !== seg));
    setParcCommandes((c) => c.filter((_, k) => k !== seg));
  };
  const majMontant = (i, v) => setParcMontants((m) => m.map((x, k) => (k === i ? String(v).replace(/\D/g, '') : x)));
  // Le retour est un MIROIR de l'aller, proposé une seule fois (sinon un 2ᵉ clic
  // empilait un aller-retour d'aller-retour). Chaque arrêt reste modifiable
  // ensuite : le livreur peut rentrer par un autre chemin.
  const retourDejaAjoute = parcArrets.length >= 3
    && parcArrets[parcArrets.length - 1].id_point === parcArrets[0].id_point;
  const ajouterRetour = () => {
    if (retourDejaAjoute) return;
    setParcArrets((a) => (a.length < 2 ? a : [...a, ...a.slice(0, -1).reverse()]));
    setParcMontants((m) => [...m, ...m.slice().reverse()]); // miroir des montants aller (éditable)
    // Le retour ne dessert personne : ses segments repartent sans commande.
    setParcCommandes((c) => [...c, ...c.map(() => null)]);
  };
  // --- BROUILLON DU CONSTRUCTEUR DE PARCOURS -------------------------------
  //
  // Un parcours, c'est cinq ou six arrêts, autant de montants, parfois une
  // commande par arrêt. Jusqu'ici, tout disparaissait dès qu'on quittait
  // l'écran — or c'est exactement ce qui arrive en tournée : on ouvre le
  // constructeur, un appel entre, l'application passe en arrière-plan, Android
  // la décharge. Le livreur retrouvait une fenêtre vide et refaisait tout.
  //
  // On garde donc la saisie en cours dans le stockage local, sous une clé liée
  // au JOUR de la feuille (un brouillon d'hier n'a rien à faire dans la feuille
  // d'aujourd'hui). L'uid du parcours est sauvegardé avec : reprendre le
  // brouillon reprend le MÊME uid, donc l'idempotence serveur tient toujours.
  const cleBrouillonParc = draftKey('parcours', date);
  const stockage = typeof window !== 'undefined' ? window.localStorage : null;

  useEffect(() => {
    if (!parcOuvert) return;
    writeDraft(stockage, cleBrouillonParc, {
      arrets: parcArrets, montants: parcMontants, commandes: parcCommandes,
      commande: parcCommande, global: parcGlobal, montantGlobal: parcMontantGlobal,
      uid: parcUid,
    });
  }, [parcOuvert, parcArrets, parcMontants, parcCommandes, parcCommande,
    parcGlobal, parcMontantGlobal, parcUid, cleBrouillonParc, stockage]);

  const reprendreBrouillon = () => {
    const d = readDraft(stockage, cleBrouillonParc);
    if (!d || !Array.isArray(d.arrets) || d.arrets.length < 1) return false;
    setParcArrets(d.arrets);
    setParcMontants(d.montants || []);
    setParcCommandes(d.commandes || []);
    setParcCommande(d.commande || null);
    setParcGlobal(Boolean(d.global));
    setParcMontantGlobal(d.montantGlobal || '');
    setParcUid(d.uid || store.nouveauUid());
    setParcOuvert(true);
    return true;
  };

  // Un brouillon n'a de sens que s'il contient plus que l'arrêt de départ posé
  // automatiquement : sinon on proposerait de « reprendre » une fenêtre vide.
  const brouillonParcours = (() => {
    const d = readDraft(stockage, cleBrouillonParc);
    return d && Array.isArray(d.arrets) && d.arrets.length >= 2 ? d : null;
  })();

  const majCommandeSegment = (i, v) => setParcCommandes((c) => {
    const n = [...c];
    while (n.length <= i) n.push(null);
    n[i] = v || null;
    return n;
  });
  // Liste triee : le tri « prise en charge » est l'ordre RENVOYE par le serveur,
  // donc on ne touche a rien ; les autres reordonnent une copie.
  const commandesTriees = useMemo(() => {
    const t = TRIS.find((x) => x.cle === triCommandes);
    return t && t.cmp ? [...commandes].sort(t.cmp) : commandes;
  }, [commandes, triCommandes]);

  // Montant global négocié → un montant par segment. L'arithmétique vit dans
  // `repartition.js` : c'est de l'argent, donc c'est testé à part.
  const montantsEffectifs = parcGlobal
    ? repartir(Number(parcMontantGlobal) || 0, poidsDesSegments(parcArrets, tarifDe)).map(String)
    : parcMontants;
  // Libellé d'une commande dans un sélecteur — le même partout, pour qu'on
  // reconnaisse la même cliente d'une liste à l'autre.
  const optCommande = (c) => `#${c.id_commande} · ${c.nom_client}${c.zone ? ` · ${c.zone}` : ''}${c.produits ? ` · ${c.produits}` : ''}`;

  const totalParcours = montantsEffectifs.reduce((s, v) => s + (Number(v) || 0), 0);
  const envoyerParcours = async () => {
    if (parcArrets.length < 2) { toast.error('Ajoutez au moins deux arrêts.'); return; }
    if (parcArrets.some((x) => !x.id_point)) { toast.error('Chaque arrêt doit être un lieu de la liste.'); return; }
    // Une commande au moins : celle par défaut, ou une par segment.
    if (!parcCommande && !parcCommandes.some(Boolean)) {
      toast.error('Choisissez au moins une commande livrée.'); return;
    }
    if (parcGlobal) {
      if (!(Number(parcMontantGlobal) > 0)) { toast.error('Saisissez le montant global négocié.'); return; }
    } else if (montantsEffectifs.some((v) => !(Number(v) > 0))) {
      toast.error('Chaque segment doit avoir un montant.'); return;
    }
    // La note dit d'où vient le montant : sans elle, on croirait plus tard que
    // chaque segment a été discuté séparément.
    const note = parcGlobal
      ? `Montant global négocié : ${fmt(Number(parcMontantGlobal))} FC pour ${parcArrets.length} arrêts`
      : undefined;
    const segments = [];
    for (let i = 0; i < parcArrets.length - 1; i += 1) {
      segments.push({
        id_point_depart: parcArrets[i].id_point,
        id_point_arrivee: parcArrets[i + 1].id_point,
        montant_fc: Number(montantsEffectifs[i]),
        id_commande: parcCommandes[i] || parcCommande || null,
        note,
      });
    }
    // HORS LIGNE (30/07) — le parcours ne part plus directement au serveur.
    //
    // Il était le SEUL geste de cet écran à exiger le réseau : sans connexion,
    // le livreur perdait sa saisie alors que tout le reste de la feuille tient
    // hors ligne. Or c'est précisément en tournée qu'on saisit un parcours.
    //
    // On écrit donc les segments dans le BROUILLON LOCAL, comme n'importe quelle
    // ligne, en leur donnant tous le même `id_parcours`. La synchro les
    // regroupera et les enverra d'un bloc à /mine/parcours (une transaction
    // serveur, un seul parcours). Le brouillon reste la source de vérité de
    // l'affichage : le parcours apparaît immédiatement, réseau ou pas.
    const idParcours = parcUid || store.nouveauUid();
    setParcEnvoi(true);
    try {
      persister((b) => {
        let lignes = b.lignes;
        segments.forEach((s) => {
          lignes = [...lignes, construireLigne({
            type: 'LIVRAISON',
            id_point_depart: s.id_point_depart,
            id_point_arrivee: s.id_point_arrivee,
            montant_fc: s.montant_fc,
            id_commande: s.id_commande,
            note: s.note,
            id_parcours: idParcours,
          }, referentiel, commandes, lignes)];
        });
        return { ...b, lignes };
      });
      setSync((s) => ({ ...s, etat: enLigne ? 'attente' : 'hors_ligne' }));
      clearDraft(stockage, cleBrouillonParc);   // il est enregistré : le brouillon n'a plus lieu d'être
      setParcOuvert(false);
      toast.success(enLigne
        ? `Parcours enregistré : ${segments.length} segments, ${fmt(totalParcours)} FC.`
        : `Parcours gardé hors ligne : ${segments.length} segments, ${fmt(totalParcours)} FC. Il partira au retour du réseau.`);
      if (enLigne) programmerSync();
    } finally { setParcEnvoi(false); }
  };

  if (chargement) {
    return <NdoePage><div className="fd-centre"><Spin size="large" /></div></NdoePage>;
  }
  if (erreur) {
    return <NdoePage><NdoeError text={erreur} onRetry={charger} /></NdoePage>;
  }

  const statutLabel = {
    SOUMISE: <Tag color="processing">en attente de validation</Tag>,
    VALIDEE: <Tag color="success">validée</Tag>,
    RENVOYEE: <Tag color="error">à corriger</Tag>,
  }[feuilleServeur?.statut] || <Tag color="default">brouillon</Tag>;

  // Bannière de synchro : dit au déclarant où en est son argent.
  const banniere = (() => {
    if (soumiseOuValidee) return null;
    if (sync.etat === 'hors_ligne') {
      return { cls: 'fd-sync--hors', txt: `Hors ligne — ${lignes.length} dépense(s) gardée(s) sur l’appareil, synchro au retour du réseau.` };
    }
    if (sync.etat === 'attente') {
      return { cls: 'fd-sync--attente', txt: 'Modifications non synchronisées…', bouton: true };
    }
    if (sync.etat === 'conflit') {
      return { cls: 'fd-sync--conflit', txt: sync.message || 'Cette feuille a déjà été traitée par le bureau.' };
    }
    return { cls: 'fd-sync--ok', txt: 'À jour' };
  })();

  return (
    <NdoePage className="feuille-depense">
      <NdoePageHeader
        eyebrow="Mes dépenses"
        title={estAujourdhui ? 'Ma feuille du jour' : 'Rattrapage'}
        subtitle={estAujourdhui ? date : libelleJourLong(date)}
        actions={statutLabel}
      />

      {/* RATTRAPAGE — choisir la journée. « Aujourd'hui » + les dernières dates
          d'ACTIVITÉ réelle du livreur (jours travaillés, pas jours calendaires). */}
      {(datesActivite.length > 0 || !estAujourdhui) && (
        <div className="fd-datenav" role="tablist" aria-label="Choisir le jour">
          <button type="button" role="tab" aria-selected={estAujourdhui}
            className={`fd-datechip${estAujourdhui ? ' on' : ''}`} onClick={() => setDate(aujourdhui())}>
            Aujourd’hui
          </button>
          {datesActivite.filter((d) => d.jour !== aujourdhui()).slice(0, 4).map((d) => (
            <button type="button" role="tab" key={d.jour} aria-selected={date === d.jour}
              className={`fd-datechip${date === d.jour ? ' on' : ''}`} onClick={() => setDate(d.jour)}>
              {libelleJourCourt(d.jour)}<small>{d.commandes}</small>
            </button>
          ))}
        </div>
      )}
      {!estAujourdhui && (
        <div className="fd-rattrapage" role="status">
          ⏪ Vous déclarez pour le <b>{libelleJourLong(date)}</b> — pas aujourd’hui.
        </div>
      )}

      {banniere ? (
        <div className={`fd-sync ${banniere.cls}`}>
          <span>{banniere.txt}</span>
          {banniere.bouton ? (
            <Button size="small" type="link" icon={<CloudSyncOutlined />} onClick={() => synchroniser()}>
              Synchroniser
            </Button>
          ) : null}
        </div>
      ) : null}

      {feuilleServeur?.statut === 'RENVOYEE' && feuilleServeur?.motif_renvoi ? (
        <div className="fd-renvoi">
          <strong>À corriger :</strong> {feuilleServeur.motif_renvoi}
        </div>
      ) : null}

      {modifiable && favoris.length > 0 ? (
        <section className="fd-bloc">
          <h2 className="fd-titre">Trajets habituels</h2>
          <div className="fd-favoris">
            {favoris.map((f) => (
              <button
                key={`${f.depart}-${f.arrivee}`}
                type="button"
                className="fd-favori"
                aria-label={`Ajouter ${f.nom_depart} vers ${f.nom_arrivee}, ${fmt(f.montant)} francs`}
                onClick={() => ajouterFavori(f)}
              >
                <span className="fd-favori-trajet">{f.nom_depart} → {f.nom_arrivee}</span>
                <span className="fd-favori-prix">{fmt(f.montant)} FC</span>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      <section className="fd-bloc">
        <h2 className="fd-titre">{estAujourdhui ? 'Déclaré aujourd’hui' : 'Déclaré ce jour-là'}</h2>
        {lignes.length === 0 ? (
          <p className="fd-vide">Aucune dépense pour l’instant. Touchez un trajet habituel, ou ajoutez-en un.</p>
        ) : (
          <ul className="fd-lignes">
            {grouperLignes(lignes).map((item) => (item.parcours ? (
              // PARCOURS multi-arrêts : résumé pliable (N segments · route · total).
              <li key={item.uid} className="fd-ligne fd-parcours">
                <button type="button" className="fd-parcours-head"
                  aria-expanded={!!parcoursOuverts[item.id_parcours]}
                  onClick={() => setParcoursOuverts((m) => ({ ...m, [item.id_parcours]: !m[item.id_parcours] }))}>
                  <div className="fd-ligne-texte">
                    <span className="fd-ligne-trajet">🧭 Parcours{item.nom_client ? ` · ${item.nom_client}` : ''}{item.id_commande ? ` · #${item.id_commande}` : ''}</span>
                    <span className="fd-ligne-detail">
                      {item.segments.length} segment(s) · {[item.segments[0]?.depart?.nom, ...item.segments.map((s) => s.arrivee?.nom)].filter(Boolean).join(' → ')}
                    </span>
                  </div>
                  <span className="fd-ligne-montant">{fmt(item.total)} FC</span>
                  <span className="fd-parcours-chev">{parcoursOuverts[item.id_parcours] ? '▲' : '▼'}</span>
                </button>
                {parcoursOuverts[item.id_parcours] && (
                  <ul className="fd-parcours-seg">
                    {item.segments.map((s, i) => (
                      <li key={s.uid} className="fd-seg">
                        <span><b className="fd-seg-n">{i + 1}</b> {libelleTrajet(s)}</span>
                        <span className="fd-seg-m">{fmt(s.montant_fc)} FC
                          {modifiable ? (
                            <button type="button" className="fd-suppr" aria-label="Retirer ce segment" onClick={() => supprimer(s)}>
                              <DeleteOutlined />
                            </button>
                          ) : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ) : (
              <li key={item.uid} className="fd-ligne">
                <div className="fd-ligne-texte">
                  <span className="fd-ligne-trajet">
                    {libelleTrajet(item)}
                    {item.arrivee?.libre || item.depart?.libre ? (
                      <>
                        {' '}
                        <span className="fd-libre" title="Lieu absent du référentiel — la supervision le rattachera">à rattacher</span>
                      </>
                    ) : null}
                  </span>
                  <span className="fd-ligne-detail">{detailLigne(item)}</span>
                </div>
                <span className="fd-ligne-montant">{fmt(item.montant_fc)} FC</span>
                {modifiable ? (
                  <button type="button" className="fd-suppr" aria-label="Retirer" onClick={() => supprimer(item)}>
                    <DeleteOutlined />
                  </button>
                ) : null}
              </li>
            )))}
          </ul>
        )}
        <div className="fd-total">
          <span>Total dépenses</span>
          <strong>{fmt(totalFc)} FC</strong>
        </div>
      </section>

      {/* CASH REÇU — fond de caisse du jour et/ou provision remise à chaque
          livraison. Rubrique présente dans ~99 % des rapports WhatsApp. */}
      <section className="fd-bloc">
        <h2 className="fd-titre">Cash reçu</h2>
        {provisions.length === 0 ? (
          <p className="fd-vide">Aucun cash reçu déclaré. Ajoutez le fond de caisse ou une provision de transport.</p>
        ) : (
          <ul className="fd-lignes">
            {provisions.map((p) => (
              <li key={p.id_provision} className="fd-ligne">
                <div className="fd-ligne-texte">
                  <span className="fd-ligne-trajet">
                    {p.motif === 'TRANSPORT' ? 'Provision transport' : p.motif === 'OPERATION' ? 'Fonds d’opération' : 'Cash reçu'}
                    {p.nom_client ? ` · ${p.nom_client}` : ''}
                  </span>
                  <span className="fd-ligne-detail">
                    {p.id_commande ? `Commande #${p.id_commande}` : 'Fond de caisse du jour'}{p.heure ? ` · ${p.heure}` : ''}
                  </span>
                </div>
                <span className="fd-ligne-montant fd-recu-montant">+{fmt(p.montant_fc)} FC</span>
                {modifiable ? (
                  <button type="button" className="fd-suppr" aria-label="Retirer" onClick={() => supprimerProvision(p)}>
                    <DeleteOutlined />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {modifiable ? (
          <Button icon={<PlusOutlined />} block style={{ marginTop: 8 }} onClick={ouvrirCash}>
            Ajouter du cash reçu
          </Button>
        ) : null}
      </section>

      {/* RAPPORT FINANCIER — même structure que les messages WhatsApp.
          Chaque devise est calculée séparément (jamais convertie). */}
      {(recuFc > 0 || recuUsd > 0 || recetteUsd > 0 || totalFc > 0) && (
        <section className="fd-bloc">
          <h2 className="fd-titre">Rapport financier</h2>
          <div className="fd-rapport">
            <div className="fd-rap-l"><span>Cash reçu</span><b>{fmt(recuFc)} FC{recuUsd > 0 ? ` + ${fmt(recuUsd)} $` : ''}</b></div>
            <div className="fd-rap-l">
              <span>Recette (encaissé clientes)</span>
              <b>{recette === null ? '—' : `${fmt(recetteUsd)} $`}</b>
            </div>
            <div className="fd-rap-l fd-rap-dep"><span>Dépenses</span><b>− {fmt(totalFc)} FC</b></div>
            <div className="fd-rap-l fd-rap-reste">
              <span>Reste à remettre</span>
              <b>
                {fmt(resteFc)} FC{resteUsd > 0 ? ` + ${fmt(resteUsd)} $` : ''}
              </b>
            </div>
          </div>
          {resteFc < 0 && (
            <p className="fd-rap-alerte">⚠ Vous avez dépensé {fmt(Math.abs(resteFc))} FC de plus que le cash reçu — la boutique vous doit ce montant.</p>
          )}
          {recette === null && (
            <p className="fd-parc-hint">La recette du jour n’a pas pu être lue (hors ligne) — elle s’ajoutera au retour du réseau.</p>
          )}
        </section>
      )}

      {modifiable ? (
        <div className="fd-actions">
          <Button icon={<PlusOutlined />} size="large" block onClick={() => setOuvert(true)}>
            Ajouter un trajet
          </Button>
          <Button icon={<NodeIndexOutlined />} size="large" block onClick={ouvrirParcours}>
            Parcours (plusieurs arrêts)
          </Button>
          {/* Une saisie interrompue se reprend, elle ne se refait pas. Le
              bouton n'apparaît que s'il y a vraiment quelque chose à reprendre
              (au moins deux arrêts), sinon c'est du bruit. */}
          {brouillonParcours && !parcOuvert && (
            <Button type="dashed" size="large" block onClick={reprendreBrouillon}>
              ↩︎ Reprendre le parcours commencé ({brouillonParcours.arrets.length} arrêts)
            </Button>
          )}
          <Button icon={<WifiOutlined />} size="large" block onClick={ajouterCommunication}>
            Forfait ({fmt(MONTANT_COMMUNICATION_DEFAUT)} FC)
          </Button>
          <Button
            type="primary" icon={<SendOutlined />} size="large" block
            onClick={soumettre} disabled={enCours || lignes.length === 0}
          >
            Soumettre la journée
          </Button>
        </div>
      ) : null}

      <Drawer
        title="Ajouter un trajet"
        placement="bottom"
        height="auto"
        open={ouvert}
        onClose={() => { setOuvert(false); reinitialiser(); }}
        className="fd-drawer"
      >
        <div className="fd-champ">
          <label>Nature</label>
          <Segmented block options={TYPES} value={type} onChange={setType} />
        </div>

        {type === 'LIVRAISON' ? (
          <div className="fd-champ">
            <label>Commande livrée</label>
            <Select
              value={idCommande}
              onChange={setIdCommande}
              showSearch
              optionFilterProp="label"
              filterOption={(input, opt) => (opt?.label || '').toLowerCase().includes(input.trim().toLowerCase())}
              placeholder={commandes.length ? 'Chercher : cliente, n°, article…' : 'Aucune livraison aujourd’hui'}
              notFoundContent="Aucune livraison ne correspond"
              disabled={!commandes.length}
              style={{ width: '100%' }}
              size="large"
              // Le libellé inclut le n°, la zone et les ARTICLES transportés : le
              // livreur reconnaît sa livraison à la paire, et la recherche locale
              // filtre sur tout ça (cliente / numéro / modèle / pointure).
              options={commandesTriees.map((c) => ({
                value: c.id_commande,
                label: `#${c.id_commande} · ${c.nom_client}${c.zone ? ` · ${c.zone}` : ''} · ${c.paires} p${c.produits ? ` · ${c.produits}` : ''}`,
              }))}
            />
            {/* Tri de la liste. N'apparaît qu'à partir de 4 commandes : en
                dessous, chercher est plus rapide que trier. */}
            {commandes.length >= 4 && (
              <div className="fd-tri">
                <span>Trier par</span>
                {TRIS.map((t) => (
                  <button
                    type="button"
                    key={t.cle}
                    className={`fd-tri__btn${triCommandes === t.cle ? ' is-actif' : ''}`}
                    onClick={() => setTriCommandes(t.cle)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : null}

        <div className="fd-champ">
          <label>Destination</label>
          <Segmented
            block
            options={[{ value: 'liste', label: 'Dans la liste' }, { value: 'libre', label: 'Autre lieu' }]}
            value={modeLieu}
            onChange={setModeLieu}
          />
          {modeLieu === 'liste' ? (
            <Select
              showSearch
              value={idArrivee}
              onChange={choisirArrivee}
              placeholder="Où êtes-vous allé ?"
              style={{ width: '100%', marginTop: 8 }}
              size="large"
              optionFilterProp="label"
              options={(referentiel.points || []).map((p) => ({ value: p.id, label: p.nom }))}
            />
          ) : (
            <>
              <Input
                value={libelleArrivee}
                onChange={(e) => setLibelleArrivee(e.target.value)}
                placeholder="Nom du lieu"
                maxLength={80}
                size="large"
                style={{ marginTop: 8 }}
              />
              <p className="fd-aide">Ce lieu sera rattaché par la supervision. Votre dépense est enregistrée normalement.</p>
            </>
          )}
        </div>

        <div className="fd-champ">
          <label>Montant (FC)</label>
          <Input
            value={montant}
            onChange={(e) => setMontant(e.target.value.replace(/[^\d]/g, ''))}
            inputMode="numeric"
            placeholder="0"
            size="large"
            suffix="FC"
          />
          {/* Fourchette du trajet. p25/médiane/p75 étaient déjà calculés et
              renvoyés par le référentiel, mais SEULE la médiane servait — en
              pré-remplissage silencieux. Un livreur qui a payé plus n'avait
              donc aucun moyen de savoir si son montant était normal, et la
              supervision aucun repère pour juger. On montre la fourchette et on
              situe la saisie dedans. */}
          {tarifCourant && (
            <div className="fd-fourchette">
              <div className="fd-fourchette__barre">
                <span className="fd-fourchette__zone" />
                {positionDansFourchette !== null && (
                  <span
                    className={`fd-fourchette__curseur${ecartTarif ? ' is-hors' : ''}`}
                    style={{ left: `${positionDansFourchette}%` }}
                  />
                )}
              </div>
              <div className="fd-fourchette__legende">
                <span>{tarifCourant.p25.toLocaleString('fr-FR')}</span>
                <b>{tarifCourant.median.toLocaleString('fr-FR')} FC</b>
                <span>{tarifCourant.p75.toLocaleString('fr-FR')}</span>
              </div>
              <p className="fd-aide">
                {ecartTarif
                  ? `Au-delà de l'habituel sur ce trajet (${tarifCourant.p25.toLocaleString('fr-FR')}–${tarifCourant.p75.toLocaleString('fr-FR')} FC sur ${tarifCourant.occurrences || '?'} courses).`
                  : `Habituel sur ce trajet : ${tarifCourant.p25.toLocaleString('fr-FR')} à ${tarifCourant.p75.toLocaleString('fr-FR')} FC${tarifCourant.occurrences ? ` (${tarifCourant.occurrences} courses)` : ''}.`}
              </p>
              {/* Le motif n'apparaît QUE si le montant dépasse l'habituel : on ne
                  demande rien quand il n'y a rien à expliquer. Facultatif — le
                  livreur ne doit pas être bloqué sur le terrain. */}
              {ecartTarif && (
                <Select
                  placeholder="Pourquoi ce montant ? (facultatif)"
                  value={motifEcart}
                  onChange={setMotifEcart}
                  allowClear
                  size="large"
                  style={{ width: '100%', marginTop: 8 }}
                  options={MOTIFS_ECART.map((m) => ({ value: m, label: m }))}
                />
              )}
            </div>
          )}
        </div>

        <div className="fd-champ">
          <label>Moyen de transport</label>
          <Segmented
            block
            options={(referentiel.moyens || []).map((m) => ({ value: m.id, label: m.libelle }))}
            value={idMoyen}
            onChange={setIdMoyen}
          />
        </div>

        <Button
          type="primary" size="large" block
          disabled={
            !montant
            || (modeLieu === 'liste' ? !idArrivee : !libelleArrivee.trim())
            || (type === 'LIVRAISON' && !idCommande)
          }
          onClick={validerFormulaire}
        >
          Enregistrer
        </Button>
      </Drawer>

      {/* CASH REÇU — fond de caisse du jour ou provision liée à une livraison. */}
      <Modal title="Cash reçu" open={cashOuvert} onCancel={() => setCashOuvert(false)} footer={null} destroyOnClose>
        <div className="fd-parc-build">
          <label className="fd-parc-lab">Motif</label>
          <Segmented
            block
            value={cashMotif}
            onChange={setCashMotif}
            options={[
              { label: 'Transport', value: 'TRANSPORT' },
              { label: 'Opération', value: 'OPERATION' },
              { label: 'Autre', value: 'AUTRE' },
            ]}
          />

          <label className="fd-parc-lab">Montant reçu</label>
          <Input
            inputMode="numeric" size="large" suffix="FC" placeholder="ex. 10 000"
            value={cashMontant} onChange={(e) => setCashMontant(e.target.value.replace(/\D/g, ''))}
          />

          <label className="fd-parc-lab">Pour une livraison précise ? (facultatif)</label>
          <Select
            allowClear
            value={cashCommande}
            onChange={setCashCommande}
            showSearch optionFilterProp="label"
            filterOption={(input, opt) => (opt?.label || '').toLowerCase().includes(input.trim().toLowerCase())}
            placeholder="Fond de caisse du jour (aucune commande)"
            style={{ width: '100%' }} size="large"
            options={commandesTriees.map((c) => ({ value: c.id_commande, label: `#${c.id_commande} · ${c.nom_client}${c.zone ? ` · ${c.zone}` : ''}` }))}
          />

          <Button type="primary" size="large" block loading={cashEnvoi} onClick={envoyerCash} style={{ marginTop: 6 }}>
            Enregistrer
          </Button>
          <p className="fd-parc-hint">Le cash reçu n’est pas une dépense : il sert à calculer ce qu’il reste à remettre.</p>
        </div>
      </Modal>

      {/* CONSTRUCTEUR DE PARCOURS multi-arrêts (A→B→C→D→C→B→A). */}
      <Modal
        title="Parcours de livraison"
        open={parcOuvert}
        onCancel={() => setParcOuvert(false)}
        footer={null}
        destroyOnClose
      >
        <div className="fd-parc-build">
          <label className="fd-parc-lab">Commande principale</label>
          <Select
            value={parcCommande}
            onChange={setParcCommande}
            showSearch optionFilterProp="label"
            filterOption={(input, opt) => (opt?.label || '').toLowerCase().includes(input.trim().toLowerCase())}
            placeholder={commandes.length ? 'Chercher : cliente, n°, article…' : 'Aucune livraison'}
            style={{ width: '100%' }} size="large"
            options={commandesTriees.map((c) => ({ value: c.id_commande, label: optCommande(c) }))}
          />
          <p className="fd-parc-hint">
            Elle s’applique à tous les segments. Si la tournée dessert plusieurs clientes,
            précisez-le arrêt par arrêt ci-dessous.
          </p>

          {/* MONTANT GLOBAL — sur le terrain on discute UN prix pour toute la
              course. Le saisir segment par segment obligeait à inventer une
              répartition de tête ; elle est faite ici, au prorata des tarifs. */}
          <label className="fd-parc-lab">Comment le prix a-t-il été convenu ?</label>
          <Radio.Group
            value={parcGlobal ? 'global' : 'segment'}
            onChange={(e) => setParcGlobal(e.target.value === 'global')}
            optionType="button" buttonStyle="solid" style={{ width: '100%' }}
          >
            <Radio.Button value="segment" style={{ width: '50%', textAlign: 'center' }}>Par segment</Radio.Button>
            <Radio.Button value="global" style={{ width: '50%', textAlign: 'center' }}>Un prix global</Radio.Button>
          </Radio.Group>
          {parcGlobal && (
            <div className="fd-parc-global">
              <Input
                inputMode="numeric"
                value={parcMontantGlobal}
                onChange={(e) => setParcMontantGlobal(e.target.value.replace(/\D/g, ''))}
                suffix="FC"
                placeholder="montant négocié pour toute la course"
                size="large"
              />
              <small>
                Réparti sur les segments au prorata des tarifs habituels — le total reste
                exactement ce que vous avez payé.
              </small>
            </div>
          )}

          <label className="fd-parc-lab">Arrêts (dans l’ordre)</label>
          <div className="fd-parc-arrets">
            {parcArrets.map((ar, i) => (
              <div key={i} className="fd-parc-arret">
                <div className="fd-parc-row">
                  <span className="fd-parc-num">{i + 1}</span>
                  <Select
                    value={ar.id_point}
                    onChange={(id) => majArret(i, id)}
                    showSearch optionFilterProp="label"
                    filterOption={(input, opt) => (opt?.label || '').toLowerCase().includes(input.trim().toLowerCase())}
                    placeholder="Choisir un lieu"
                    style={{ flex: 1 }}
                    options={pointsRef.map((p) => ({ value: p.id, label: p.nom }))}
                  />
                  {parcArrets.length > 1 && (
                    <button type="button" className="fd-suppr" aria-label="Retirer l’arrêt" onClick={() => retirerArret(i)}><DeleteOutlined /></button>
                  )}
                </div>
                {i < parcArrets.length - 1 && (
                  <div className="fd-parc-seg">
                    <span className="fd-parc-fleche">↓ segment {i + 1}</span>
                    {parcGlobal ? (
                      // En prix global, le montant du segment est CALCULÉ : on
                      // l'affiche pour que le livreur voie la répartition, mais
                      // il n'y a rien à saisir.
                      <span className="fd-parc-part num">{fmt(Number(montantsEffectifs[i]) || 0)} FC</span>
                    ) : (
                      <Input
                        inputMode="numeric"
                        value={parcMontants[i] || ''}
                        onChange={(e) => majMontant(i, e.target.value)}
                        suffix="FC"
                        placeholder="montant"
                        style={{ maxWidth: 130 }}
                      />
                    )}
                    <Select
                      value={parcCommandes[i] || undefined}
                      onChange={(v) => majCommandeSegment(i, v)}
                      allowClear
                      showSearch optionFilterProp="label"
                      filterOption={(input, opt) => (opt?.label || '').toLowerCase().includes(input.trim().toLowerCase())}
                      placeholder="même commande"
                      style={{ flex: 1, minWidth: 150 }}
                      options={commandesTriees.map((c) => ({ value: c.id_commande, label: optCommande(c) }))}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="fd-parc-actions">
            <Button icon={<PlusOutlined />} onClick={ajouterArret}>Ajouter un arrêt</Button>
            <Button onClick={ajouterRetour} disabled={parcArrets.length < 2 || retourDejaAjoute}>
              {retourDejaAjoute ? '✓ Retour ajouté' : '↩︎ Ajouter le retour'}
            </Button>
          </div>

          <div className="fd-parc-total">
            <span>{Math.max(0, parcArrets.length - 1)} segment(s)</span>
            <strong>{fmt(totalParcours)} FC</strong>
          </div>

          <Button type="primary" size="large" block loading={parcEnvoi} onClick={envoyerParcours}>
            Enregistrer le parcours
          </Button>
          <p className="fd-parc-hint">Le retour est proposé, jamais imposé. Tout est gardé même sans réseau.</p>
        </div>
      </Modal>
    </NdoePage>
  );
};

export default FeuilleDepense;
